import React from "react";
import asModal from "lib/modal/asModal";
import BroadcastServices from "lib/BroadcastServices";
import Calendar from "lib/common/descriptorcontrols/DateTimeField/calendar";
import Textarea from "lib/common/descriptorcontrols/StringField/textarea";
import ControlTemplate
    from "lib/common/descriptorcontrols/BaseControls/ControlTemplate";
import ActionButton
    from "lib/common/descriptorcontrols/BaseControls/ActionButton";
import Button from "lib/common/descriptorcontrols/BaseControls/Button";
import classnames from "classnames";
import InlineLoader from "views/InlineLoader";
import usePeriodicTask from "lib/usePeriodicTask";

function emptyMessage() {
    return {
        activeAt: null,
        expiresAt: moment().add("hours", 1),
        message: "",
    };
}

function formatMoment(m) {
    let s = m.fromNow();
    if (Math.abs(m - moment()) < 12 * 60 * 60 * 1000) {
        s += ` (${m.format("HH:mm")})`;
    }
    return s;
}

export function BroadcastAdmin() {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(undefined);
    const [allMessages, setAllMessages] = React.useState([]);
    const [lastRefresh, setLastRefresh] = React.useState(0);
    const refresh = background => {
        if (background && lastRefresh > Date.now() - 15 * 1000) return;
        setLoading(true);
        BroadcastServices.getAllMessages()
            .then(ms => {
                setAllMessages(ms);
                setLastRefresh(Date.now());
            })
            .catch(setError)
            .finally(() => setLoading(false));
    };

    usePeriodicTask(
        () => refresh(true),
        30 * 1000,
        [lastRefresh]
    );

    const [draft, setDraft] = React.useState(emptyMessage());
    const resetForm = () =>
        setDraft(emptyMessage());

    const handleCommit = (desc, val) => {
        setDraft(d => ({
            ...d,
            [desc.key]: val,
        }));
    };

    const handleChange = (e, desc, val) =>
        handleCommit(desc, val);

    const handleReset = e => {
        e.preventDefault();
        resetForm();
    };

    const handleCreate = e => {
        e.preventDefault();
        setLoading(true);
        BroadcastServices.createMessage(draft)
            .then(() => refresh(false))
            .then(resetForm)
            .catch(setError);
    };

    const handleDelete = (e, m) => {
        e.preventDefault();
        setLoading(true);
        BroadcastServices.deleteMessage(m)
            .then(() => refresh(false))
            .catch(setError);
    };

    const enableBroadcast = !loading && !!draft.message && !!draft.expiresAt;
    const now = moment();

    return <React.Fragment>
        <div className="broadcast-title-warning">
            Note: This will send a message to all logged in users.
        </div>
        <div className="record-grid-container">
            <div className="record-grid">
                <table className="table-alt table-condensed">
                    <thead>
                    <tr>
                        <th>
                            {loading && <InlineLoader message={null} />}
                            Active
                        </th>
                        <th>Expires</th>
                        <th>Message</th>
                        <th />
                    </tr>
                    </thead>
                    <tbody>
                    {allMessages.length
                        ? allMessages.map((m, i) => <tr
                            key={m.id}
                            className={classnames({
                                "broadcast-past": m.expiresAt < now,
                                "broadcast-present": m.activeAt <= now && m.expiresAt >= now,
                                "broadcast-future": m.activeAt > now,
                            })}
                        >
                            <td title={m.activeAt.format()}>{formatMoment(m.activeAt)}</td>
                            <td title={m.expiresAt.format()}>{formatMoment(m.expiresAt)}</td>
                            <td>{m.message}</td>
                            <td align={"right"}>
                                <ActionButton
                                    actionType="remove"
                                    onClick={e => handleDelete(e, m)}
                                />
                            </td>
                        </tr>)
                        : <tr>
                            <td colSpan={4}>
                                No Broadcast Messages
                            </td>
                        </tr>
                    }
                    </tbody>
                    {error && <tfoot>
                    <tr>
                        <td
                            colSpan={4}
                            className="alert-danger"
                        >
                            {error.message || error.statusText || error.toString()}
                        </td>
                    </tr>
                    </tfoot>}
                </table>
            </div>
        </div>

        <form
            onSubmit={handleCreate}
            style={{
                padding: "2em 0",
                margin: "2em 0",
                borderTop: "1px solid gray",
            }}
        >
            <Calendar
                descriptor={{
                    id: "broadcast-activeAt",
                    key: "activeAt",
                    type: "datetime",
                    control: "calendar",
                    label: "Active At",
                    disableBehaviour: "disable",
                    config: {
                        granularity: "MINUTE",
                    },
                }}
                value={draft.activeAt}
                onCommitChange={handleCommit}
                enabled
            />
            <Calendar
                descriptor={{
                    id: "broadcast-expiresAt",
                    key: "expiresAt",
                    type: "datetime",
                    control: "calendar",
                    label: "Expires At",
                    disableBehaviour: "disable",
                    config: {
                        granularity: "MINUTE",
                    },
                    required: true,
                }}
                value={draft.expiresAt}
                onCommitChange={handleCommit}
                enabled
            />
            <Textarea
                descriptor={{
                    id: "broadcast-message",
                    key: "message",
                    type: "string",
                    control: "textarea",
                    label: "Message",
                    disableBehaviour: "disable",
                    config: {},
                    required: true,
                }}
                value={draft.message}
                onChange={handleChange}
                onCommitChange={handleCommit}
                enabled
            />
            <ControlTemplate
                descriptor={{
                    id: "broadcast-actions",
                    key: "actions",
                    type: "widget",
                    control: "button",
                    label: "",
                    disableBehaviour: "disable",
                    config: {},
                }}
            >
                <Button
                    label="Broadcast"
                    onClick={handleCreate}
                    className="btn btn-primary"
                    enabled={enableBroadcast}
                />
                <Button
                    label="Reset Form"
                    onClick={handleReset}
                    className="btn btn-default"
                    enabled
                />
            </ControlTemplate>
        </form>
        <br clear="both" />
    </React.Fragment>;
}

export const BackboneBroadcastAdmin = asModal("Broadcast Admin")(
    BroadcastAdmin);



// WEBPACK FOOTER //
// ./app/lib/broadcast/BroadcastAdminModule.js